import { PropsWithChildren, useCallback, useContext, useMemo } from "react";

import { CSSProperties, css } from "@compiled/react";
import { defineMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { isLocalizableHref, useLocalizedHref } from "@/libraries/web-framework";
import { TypographyStyles } from "@/libraries/wpl/foundations/src";
import {
    LogoV3,
    LogoBrandV3,
    ProductKeyTypes
} from "@/libraries/wpl/foundations/src";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { MotionContainer } from "./components/motion-container";
import { RovoSection } from "./components/rovo-section";
import { GlobalNavProductRecommendations } from "./global-nav-product-recommendations";
import { NavProductRecommendationsContext } from "./global-nav-product-recommendations/context/nav-product-recommendations-context";
import { SubnavWithSidebarTabs } from "./layouts/subnav-with-sidebar";
import { useGlobalNavContext } from "./nav-links-container";
import { ProductFeaturedCollectionSubnav } from "./product-featured-collection-subnav";

export const ProductCategory = {
    Featured: 0,
    Developers: 1,
    ProductManagers: 2,
    ITProfessionals: 3,
    BusinessTeams: 4,
    LeadershipTeams: 5
} as const;

export const Treatment = {
    New: 0,
    Pending: 1
} as const;

type Category = {
    id: ProductCategory;
    title: ReturnType<typeof defineMessage>;
};

export const categories = {
    [ProductCategory.Featured]: {
        id: ProductCategory.Featured,
        title: defineMessage({
            id: "globalnav.v2.products.tabs.everyone",
            defaultMessage: "Featured"
        })
    },
    [ProductCategory.Developers]: {
        id: ProductCategory.Developers,
        title: defineMessage({
            id: "globalnav.v2.products.tabs.developers",
            defaultMessage: "Developers"
        })
    },
    [ProductCategory.ProductManagers]: {
        id: ProductCategory.ProductManagers,
        title: defineMessage({
            id: "globalnav.v2.products.tabs.product-managers",
            defaultMessage: "Product Managers"
        })
    },
    [ProductCategory.ITProfessionals]: {
        id: ProductCategory.ITProfessionals,
        title: defineMessage({
            id: "globalnav.v2.products.tabs.it-professionals",
            defaultMessage: "IT professionals"
        })
    },
    [ProductCategory.BusinessTeams]: {
        id: ProductCategory.BusinessTeams,
        title: defineMessage({
            id: "globalnav.v2.products.tabs.business-teams",
            defaultMessage: "Business Teams"
        })
    },
    [ProductCategory.LeadershipTeams]: {
        id: ProductCategory.LeadershipTeams,
        title: defineMessage({
            id: "globalnav.v2.products.tabs.leadership-teams",
            defaultMessage: "Leadership Teams"
        })
    }
} as const satisfies Record<ProductCategory, Category>;

export type Product = {
    id: string;
    name: string;
    description: ReturnType<typeof defineMessage>;
    tags: ReadonlyArray<ProductCategory>;
    logo: LogoBrandV3;
    href: string;
    isCollection?: boolean;
    isBlankSpace?: boolean;
    treatment?: Treatment;
    associatedProducts?: string[];
    order?: Partial<Record<ProductCategory, number>>;
};

export type ProductsByTag = Record<ProductCategory, Array<Product>>;

export function useProductData() {
    const { locale } = useIntl();

    const gwp7850GlobalNavChangesForTeam25Eu = checkFeatureFlagGate(
        featureFlags.gwp7850GlobalNavChangesForTeam25Eu
    );
    const loomUrl = checkFeatureFlagGate(featureFlags.wacProductsLoomURL)
        ? "/software/loom"
        : "//loom.com";

    // we could move this to top of the module, but expecting this structure will
    // be derived from dynamic data at some point
    const products: {
        [key: string]: Product;
    } = useMemo(() => {
        return {
            jira: {
                id: "jira",
                name: "Jira",
                description: defineMessage({
                    id: "globalnav.v2.products.jira.description",
                    defaultMessage: "Flexible project management"
                }),
                href: "/software/jira",
                tags: gwp7850GlobalNavChangesForTeam25Eu
                    ? [
                          ProductCategory.Developers,
                          ProductCategory.ProductManagers,
                          ProductCategory.BusinessTeams
                      ]
                    : [
                          ProductCategory.Featured,
                          ProductCategory.Developers,
                          ProductCategory.ProductManagers,
                          ProductCategory.BusinessTeams,
                          ProductCategory.LeadershipTeams
                      ],
                logo: "jira",
                order: {
                    [ProductCategory.Developers]: 1,
                    [ProductCategory.LeadershipTeams]: 2,
                    [ProductCategory.ProductManagers]: 1
                }
            },
            confluence: {
                id: "confluence",
                name: "Confluence",
                description: defineMessage({
                    id: "globalnav.v2.products.confluence.description",
                    defaultMessage: "Knowledge, all in one place"
                }),
                href: "/software/confluence",
                tags: gwp7850GlobalNavChangesForTeam25Eu
                    ? [
                          ProductCategory.ProductManagers,
                          ProductCategory.BusinessTeams
                      ]
                    : [
                          ProductCategory.Featured,
                          ProductCategory.ProductManagers,
                          ProductCategory.BusinessTeams,
                          ProductCategory.LeadershipTeams
                      ],
                logo: "confluence",
                order: {
                    [ProductCategory.LeadershipTeams]: 4,
                    [ProductCategory.ProductManagers]: 2
                }
            },
            jsm: {
                id: "jsm",
                name: "Jira Service Management",
                description: defineMessage({
                    id: "globalnav.v2.products.jsm.description",
                    defaultMessage: "Deliver service at high velocity"
                }),
                href: "/software/jira/service-management",
                tags: [
                    ProductCategory.ITProfessionals,
                    ProductCategory.BusinessTeams
                ],
                logo: "jiraservicemanagement",
                order: {
                    [ProductCategory.BusinessTeams]: 5
                }
            },
            customerservicemanagement: {
                id: "customerservicemanagement",
                name: "Customer Service Management",
                description: defineMessage({
                    id: "globalnav.v2.products.customerservicemanagement.description",
                    defaultMessage: "Customer experiences reimagined"
                }),
                href: "/software/customer-service-management",
                tags: [ProductCategory.BusinessTeams],
                logo: "customerservicemanagement",
                order: {
                    [ProductCategory.BusinessTeams]: 6
                }
            },
            trello: {
                id: "trello",
                name: "Trello",
                description: defineMessage({
                    id: "globalnav.v2.products.trello.description",
                    defaultMessage: "Organized & visualized work"
                }),
                href: "//trello.com/home",
                tags: [ProductCategory.BusinessTeams],
                logo: "trello"
            },
            rovo: {
                id: "rovo",
                name: "Rovo",
                description: defineMessage({
                    id: "globalnav.v2.products.rovo.description",
                    defaultMessage: "Transform teamwork with AI"
                }),
                href: `/software/rovo`,
                tags: [],
                logo: "rovo"
            },
            jpd: {
                id: "jpd",
                name: "Jira Product Discovery",
                description: defineMessage({
                    id: "globalnav.v2.products.jpd.description",
                    defaultMessage: "Capture & prioritize ideas"
                }),
                href: "/software/jira/product-discovery",
                tags: [ProductCategory.ProductManagers],
                logo: "jiraproductdiscovery",
                order: {
                    [ProductCategory.ProductManagers]: 3
                }
            },
            compass: {
                id: "compass",
                name: "Compass",
                description: defineMessage({
                    id: "globalnav.v2.products.compass.description",
                    defaultMessage: "Software catalog for teams"
                }),
                href: "/software/compass",
                tags: [ProductCategory.Developers],
                logo: "compass",
                order: {
                    [ProductCategory.Developers]: 2
                }
            },
            guard: {
                id: "guard",
                name: "Guard",
                description: defineMessage({
                    id: "globalnav.v2.products.guard.description",
                    defaultMessage: "Enhanced cloud security"
                }),
                href: "/software/guard",
                tags: [ProductCategory.ITProfessionals],
                logo: "guard"
            },
            loom: {
                id: "loom",
                name: "Loom",
                description: defineMessage({
                    id: "globalnav.v2.products.loom.description",
                    defaultMessage: "Quick, async video updates"
                }),
                href: loomUrl,
                tags: gwp7850GlobalNavChangesForTeam25Eu
                    ? [ProductCategory.BusinessTeams]
                    : [
                          ProductCategory.Featured,
                          ProductCategory.BusinessTeams,
                          ProductCategory.LeadershipTeams
                      ],
                logo: "loom",
                order: {
                    [ProductCategory.LeadershipTeams]: 6
                }
            },
            bitbucket: {
                id: "bitbucket",
                name: "Bitbucket",
                description: defineMessage({
                    id: "globalnav.v2.products.bitbucket.description",
                    defaultMessage: "Source code and CI/CD"
                }),
                href: "/software/bitbucket",
                tags: [ProductCategory.Developers],
                logo: "bitbucket",
                order: {
                    [ProductCategory.Developers]: 4
                }
            },
            jiraalign: {
                id: "jiraalign",
                name: "Align",
                description: defineMessage({
                    id: "globalnav.v2.products.jiraalign.description",
                    defaultMessage: "Enterprise-wide work planning & value"
                }),
                href: "/software/jira/align",
                tags: [ProductCategory.LeadershipTeams],
                logo: "jiraalign",
                order: {
                    [ProductCategory.LeadershipTeams]: 3
                }
            },
            focus: {
                id: "focus",
                logo: "focus",
                href: "/software/focus",
                name: "Focus",
                description: defineMessage({
                    id: "globalnav.v2.products.focus.description",
                    defaultMessage: "Enterprise-scale strategic planning"
                }),
                tags: [ProductCategory.LeadershipTeams],
                order: {
                    [ProductCategory.LeadershipTeams]: 1
                }
            },
            talent: {
                id: "talent",
                logo: "talent",
                href: "/software/talent",
                name: "Talent",
                description: defineMessage({
                    id: "globalnav.v2.products.talent.description",
                    defaultMessage: "Knowledge workforce planning"
                }),
                tags: [ProductCategory.LeadershipTeams],
                order: gwp7850GlobalNavChangesForTeam25Eu
                    ? {
                          [ProductCategory.LeadershipTeams]: 2
                      }
                    : {
                          [ProductCategory.LeadershipTeams]: 5
                      }
            },
            dx: {
                id: "dx",
                logo: "dx",
                href: "/collections/software",
                name: "DX",
                description: defineMessage({
                    id: "globalnav.v2.products.dx.description",
                    defaultMessage: "Measure productivity and AI impact"
                }),
                tags: gwp7850GlobalNavChangesForTeam25Eu
                    ? [ProductCategory.Developers]
                    : [],
                order: {
                    [ProductCategory.Developers]: 5
                }
            },
            pipelines: {
                id: "pipelines",
                logo: "pipelines",
                href: "/software/bitbucket/features/pipelines",
                name: "Pipelines",
                description: defineMessage({
                    id: "globalnav.v2.products.pipelines.description",
                    defaultMessage: "Scalable CI/CD automation"
                }),
                tags: gwp7850GlobalNavChangesForTeam25Eu
                    ? [ProductCategory.Developers]
                    : [],
                order: {
                    [ProductCategory.Developers]: 3
                }
            },
            rovodev: {
                id: "rovodev",
                logo: "rovodev",
                href: "/software/rovo-dev",
                name: "Rovo Dev",
                description: defineMessage({
                    id: "globalnav.v2.products.rovodev.description",
                    defaultMessage: "Agentic AI for developers"
                }),
                tags: gwp7850GlobalNavChangesForTeam25Eu
                    ? [ProductCategory.Developers]
                    : [],
                order: {
                    [ProductCategory.Developers]: 6
                }
            },
            collectionteamwork: {
                id: "collectionteamwork",
                logo: "collectionteamwork",
                href: "/collections/teamwork",
                name: "Teamwork Collection",
                description: defineMessage({
                    id: "globalnav.v2.products.collectionteamwork.description",
                    defaultMessage: "Supercharge teamwork seamlessly"
                }),
                tags: gwp7850GlobalNavChangesForTeam25Eu
                    ? [
                          ProductCategory.Featured,
                          ProductCategory.ProductManagers,
                          ProductCategory.BusinessTeams
                      ]
                    : [],
                isCollection: true,
                associatedProducts: ["Jira", "Confluence", "Loom"],
                order: {
                    [ProductCategory.ProductManagers]: 5,
                    [ProductCategory.BusinessTeams]: 7
                }
            },
            collectionstrategy: {
                id: "collectionstrategy",
                logo: "collectionstrategy",
                href: "/collections/strategy",
                name: "Strategy Collection",
                description: defineMessage({
                    id: "globalnav.v2.products.collectionstrategy.description",
                    defaultMessage: "Optimize strategy and outcomes confidently"
                }),
                tags: gwp7850GlobalNavChangesForTeam25Eu
                    ? [
                          ProductCategory.Featured,
                          ProductCategory.LeadershipTeams
                      ]
                    : [],
                isCollection: true,
                associatedProducts: ["Focus", "Talent", "Align"],
                order: {
                    [ProductCategory.LeadershipTeams]: 5
                }
            },
            collectionservice: {
                id: "collectionservice",
                logo: "collectionservice",
                href: "/collections/service",
                name: "Service Collection",
                description: defineMessage({
                    id: "globalnav.v2.products.collectionservice.description",
                    defaultMessage: "Deliver service at high-velocity"
                }),
                tags: [ProductCategory.Featured],
                isCollection: true,
                associatedProducts: [
                    "Jira Service Management",
                    "Customer Service Management",
                    "Assets"
                ],
                order: {}
            },
            collectionsoftware: {
                id: "collectionsoftware",
                logo: "collectionsoftware",
                href: "/collections/software",
                name: "Software Collection",
                description: defineMessage({
                    id: "globalnav.v2.products.collectionsoftware.description",
                    defaultMessage: "Ship high-quality software fast"
                }),
                tags: gwp7850GlobalNavChangesForTeam25Eu
                    ? [ProductCategory.Featured, ProductCategory.Developers]
                    : [],
                isCollection: true,
                associatedProducts: [
                    "Rovo Dev",
                    "DX",
                    "Pipelines",
                    "Bitbucket",
                    "Compass"
                ],
                order: {
                    [ProductCategory.Developers]: 7
                }
            },
            blankspace: {
                id: "blankspace",
                name: "",
                logo: "jira",
                href: "",
                description: defineMessage({
                    id: "globalnav.v2.products.blankspace.description",
                    defaultMessage: ""
                }),
                tags: gwp7850GlobalNavChangesForTeam25Eu
                    ? [
                          ProductCategory.ProductManagers,
                          ProductCategory.LeadershipTeams
                      ]
                    : [],
                isBlankSpace: true,
                order: {
                    [ProductCategory.ProductManagers]: 4,
                    [ProductCategory.LeadershipTeams]: 4
                }
            }
        } as const satisfies Record<
            string,
            Product & { order?: Partial<Record<ProductCategory, number>> }
        >;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locale]);

    const productsByTag = useMemo(() => {
        const productsByTag = Object.values(products).reduce<ProductsByTag>(
            (acc, product) => {
                product.tags.forEach((tag) => {
                    acc[tag] = acc[tag].concat(product);
                });

                return acc;
            },
            {
                [ProductCategory.Featured]: [],
                [ProductCategory.Developers]: [],
                [ProductCategory.ProductManagers]: [],
                [ProductCategory.ITProfessionals]: [],
                [ProductCategory.BusinessTeams]: [],
                [ProductCategory.LeadershipTeams]: []
            }
        );
        for (const key in productsByTag) {
            productsByTag[key as unknown as ProductCategory].sort(
                (a: Product, b: Product) => {
                    const categoryKey: ProductCategory =
                        key as unknown as ProductCategory;
                    const aOrder: number =
                        a.order && categoryKey in a.order
                            ? a.order[categoryKey] ?? 0
                            : 0;
                    const bOrder: number =
                        b.order && categoryKey in b.order
                            ? b.order[categoryKey] ?? 0
                            : 0;
                    return aOrder - bOrder;
                }
            );
        }

        return productsByTag;
    }, [products]);

    return {
        products,
        productsByTag
    };
}

export const Pending = () => {
    const intl = useIntl();
    return (
        <span
            css={css({
                fontWeight: "regular",
                color: wacToken("color.neutral.light800"),
                fontSize: "0.75rem"
            })}
        >
            {intl.formatMessage({
                id: "globalnav.treatment.pending",
                defaultMessage: "(Pending acquisition)"
            })}
        </span>
    );
};

export const Pill = ({
    color = wacToken("color.secondary.navy"),
    backgroundColor = wacToken("color.extended.blue200"),
    children
}: PropsWithChildren<Pick<CSSProperties, "color" | "backgroundColor">>) => {
    return (
        <div
            css={css({
                display: "flex",
                alignItems: "center",
                paddingInline: "6px",
                paddingBlock: "2px",
                borderRadius: "24px"
            })}
            style={{
                color,
                backgroundColor
            }}
        >
            {children}
        </div>
    );
};

const treatments = {
    [Treatment.New]: (
        <Pill>
            <span
                css={css({
                    fontSize: "10px",
                    fontWeight: 700,
                    textTransform: "uppercase"
                })}
            >
                New
            </span>
        </Pill>
    ),
    [Treatment.Pending]: <Pending />
};

const productItemCss = css({
    display: "flex",
    maxWidth: "312px",
    paddingBlockStart: token("space.300"),
    paddingBlockEnd: token("space.300"),
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [`.logo-container`]: {
        height: "44px",
        width: "44px"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [`p`]: {
        fontFamily: wacToken("font.charlie.display"),
        margin: 0,
        fontSize: "12px",
        fontWeight: 400,
        color: wacToken("color.neutral.light800"),
        lineHeight: "normal"
    },

    [`&.collection-container`]: {
        width: "auto",
        paddingBlockEnd: 0,
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [`.logo-container`]: {
            width: "auto",
            height: "auto"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [`p`]: {
            fontFamily: wacToken("font.charlie.text")
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [`.product-header`]: {
        fontSize: "0.875rem",
        lineHeight: 1,
        display: "flex",
        flexWrap: "nowrap",
        alignItems: "flex-end",
        margin: 0,
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        strong: {
            color: wacToken("color.primary.black")
        }
    }
});

const associatedProductsCss = css({
    fontSize: "0.75rem",
    lineHeight: 1.3,
    fontFamily: wacToken("font.charlie.text"),
    color: wacToken("color.neutral.light800"),
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [`span + span:before`]: {
        content: "",
        verticalAlign: "middle",
        display: "inline-block",
        height: 0,
        lineHeight: 0,
        borderRadius: "50%",
        margin: "0 5px 4px",
        border: "1.5px solid"
    }
});

export const ProductItem = ({
    id,
    logo,
    name,
    treatment,
    isCollection,
    description,
    href,
    associatedProducts,
    isBlankSpace
}: Product) => {
    const intl = useIntl();
    const localizedHref = useLocalizedHref(href);
    const formattedHref = isLocalizableHref(href) ? localizedHref : href;

    if (isBlankSpace) {
        return <div className="blank-space">&nbsp;</div>;
    }

    return (
        <li
            id={id}
            className={isCollection ? "collection-container" : ""}
            css={productItemCss}
        >
            <a
                className={isCollection ? "collection-link" : ""}
                css={css({
                    "display": "flex",
                    "alignItems": "center",
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    "&:hover .product-header strong": {
                        color: wacToken("color.extended.blue700")
                    },
                    [`&.collection-link`]: {
                        backgroundColor: wacToken("color.neutral.light100"),
                        padding: token("space.300"),
                        width: "100%",
                        borderRadius: wacToken("corner.radius.card"),
                        flexDirection: "column",
                        alignItems: "flex-start",
                        boxShadow: `0 0 0 0 ${wacToken(
                            "color.neutral.light100"
                        )}`,
                        transition: "box-shadow 0.25s",
                        [`&:hover`]: {
                            boxShadow: token("elevation.shadow.overlay")
                        }
                    }
                })}
                href={formattedHref}
                target={isLocalizableHref(href) ? "" : "_blank"}
                rel={isLocalizableHref(href) ? "" : "noreferrer"}
                aria-label={name}
                data-event="clicked"
                data-event-component="linkButton"
                data-event-container="globalNavSubnav"
                data-product-key={id}
            >
                <div
                    className="logo-container"
                    css={css({
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginInlineEnd: token("space.150"),
                        borderRadius: "4px",
                        alignSelf: "baseline"
                    })}
                >
                    <LogoV3
                        brand={logo}
                        type="mark"
                        appearance="brand"
                        width={isCollection ? undefined : 32}
                        height={isCollection ? undefined : 32}
                    />
                </div>
                <div
                    className="product-description-container"
                    css={css({
                        display: "flex",
                        flexDirection: "column",
                        gap: token("space.075")
                    })}
                >
                    <div className="product-header">
                        {!isCollection && (
                            <strong css={css({ marginInlineEnd: "6px" })}>
                                {name}
                            </strong>
                        )}
                        {typeof treatment !== "undefined"
                            ? treatments[treatment]
                            : null}
                    </div>
                    <p>{intl.formatMessage(description)}</p>
                    {isCollection &&
                        associatedProducts &&
                        associatedProducts.length > 0 && (
                            <div css={associatedProductsCss}>
                                {associatedProducts.map((product) => (
                                    <span key={product}>{product}</span>
                                ))}
                            </div>
                        )}
                </div>
            </a>
        </li>
    );
};

export const ProductGrid = ({
    children,
    isRovoTitle
}: PropsWithChildren<{ isRovoTitle?: boolean }>) => {
    const gwp7850GlobalNavChangesForTeam25Eu = checkFeatureFlagGate(
        featureFlags.gwp7850GlobalNavChangesForTeam25Eu
    );
    const intl = useIntl();
    return (
        <div
            css={css({
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between"
            })}
        >
            <ul
                className={
                    gwp7850GlobalNavChangesForTeam25Eu
                        ? "rovo-section-enabled"
                        : ""
                }
                css={css({
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    columnGap: token("space.400"),
                    paddingInline: `${token("space.400")} ${token(
                        "space.300"
                    )}`,
                    paddingBlockStart: token("space.500"),
                    paddingBlockEnd: token("space.500"),
                    margin: 0,
                    listStyle: "none",
                    [`&.rovo-section-enabled`]: {
                        columnGap: token("space.300"),
                        paddingBlockEnd: token("space.300"),
                        [media.below.md]: {
                            paddingInline: "0 0",
                            paddingBlockStart: 0
                        }
                    },
                    [media.below.md]: {
                        display: "flex",
                        flexDirection: "column"
                    }
                })}
            >
                {children}
            </ul>

            {isRovoTitle && (
                <div
                    css={css({
                        textTransform: "uppercase",
                        fontSize: "0.75rem",
                        margin: `0 ${token("space.400")} ${token("space.200")}`,
                        [media.below.md]: {
                            margin: `0 0 ${token("space.200")}`
                        }
                    })}
                >
                    {intl.formatMessage({
                        id: "GlobalNav.RovoSection.PoweredBy",
                        defaultMessage: "Powered by"
                    })}
                </div>
            )}
            {gwp7850GlobalNavChangesForTeam25Eu && <RovoSection />}
        </div>
    );
};

const FeaturedTemplate = (props: {
    isRecs: boolean;
    isCollections: boolean;
}) => {
    if (props.isCollections) {
        return <ProductFeaturedCollectionSubnav isMobile={false} />;
    } else if (props.isRecs) {
        return <GlobalNavProductRecommendations />;
    } else {
        return <></>;
    }
};

export const ProductSubnav = ({
    productKey = "home"
}: {
    productKey?: ProductKeyTypes;
}) => {
    const intl = useIntl();

    const { productsByTag } = useProductData();

    const { direction } = useGlobalNavContext();

    const {
        shouldShowGlobalNavProductRecommendations,
        fireGlobalNavProductRecoEventsWhenInView
    } = useContext(NavProductRecommendationsContext);

    const featuredProductIndex = useMemo(
        () =>
            Object.values(categories).findIndex(
                (category) => category.id === ProductCategory.Featured
            ),
        []
    );
    const onTabInView = useCallback(
        (index: number) => {
            if (featuredProductIndex === index) {
                fireGlobalNavProductRecoEventsWhenInView({
                    isMobile: false
                });
            }
        },
        [featuredProductIndex, fireGlobalNavProductRecoEventsWhenInView]
    );

    return (
        <TypographyStyles>
            <MotionContainer direction={direction}>
                <SubnavWithSidebarTabs
                    tabs={Object.values(categories).map((category) => {
                        return {
                            id: category.id.toString(),
                            title: intl.formatMessage(category.title),
                            content:
                                category.id === ProductCategory.Featured ? (
                                    <FeaturedTemplate
                                        isRecs={
                                            shouldShowGlobalNavProductRecommendations
                                        }
                                        isCollections={true}
                                    />
                                ) : (
                                    <ProductGrid>
                                        {productsByTag[category.id].map(
                                            (product) => (
                                                <ProductItem
                                                    key={product.id}
                                                    {...product}
                                                />
                                            )
                                        )}
                                    </ProductGrid>
                                )
                        };
                    })}
                    onTabInView={onTabInView}
                    links={
                        <div
                            css={css({
                                gridArea: "tablist",
                                display: "flex",
                                alignItems: "center",
                                height: "max-content",
                                paddingInlineStart: token("space.500"),
                                paddingInlineEnd: token("space.200")
                            })}
                        >
                            <div
                                css={css({
                                    paddingInlineStart: token("space.200"),
                                    // overriding the <Link> styling
                                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                    a: {
                                        fontSize: "14px"
                                    }
                                })}
                            >
                                <Link
                                    href={useLocalizedHref("/software")}
                                    text={intl.formatMessage({
                                        id: "globalnav.v2.subnav.tabs.see-all-apps",
                                        defaultMessage: "See all apps"
                                    })}
                                    aria-label={intl.formatMessage({
                                        id: "globalnav.v2.subnav.tabs.see-all-apps-arialabel",
                                        defaultMessage: "See all apps link"
                                    })}
                                    ariaLabel={intl.formatMessage({
                                        id: "globalnav.v2.subnav.tabs.see-all-apps-arialabel",
                                        defaultMessage: "See all apps link"
                                    })}
                                    dataEventProps={{
                                        "data-product-key": productKey,
                                        "data-event-component": "linkButton",
                                        "data-event-container":
                                            "navbarDropdown",
                                        "data-label": "See all products",
                                        "data-event": "clicked"
                                    }}
                                    type="primary"
                                />
                            </div>
                        </div>
                    }
                />
            </MotionContainer>
        </TypographyStyles>
    );
};

export type ProductCategory =
    (typeof ProductCategory)[keyof typeof ProductCategory];

export type Treatment = (typeof Treatment)[keyof typeof Treatment];
